import axios from "axios";
import i18n from "i18next";
import { ACCESS_TOKEN, IMPERSONATION } from "../constants/commonConst";
import { forgetPushSubscription } from "../utils/pushSubscription";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Token utility functions
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    // Decode JWT payload (second part of the token)
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();

    // Consider token expired if it expires within the next 30 seconds
    return currentTime >= expirationTime - 30000;
  } catch (error) {
    console.error("Error decoding token:", error);
    return true;
  }
};

const getTokenExpirationTime = (token) => {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return new Date(payload.exp * 1000);
  } catch (error) {
    return null;
  }
};

// --- "Keep me signed in" ----------------------------------------------------
//
// A sign-in with the box ticked gets a 15-minute token marked `renewable`, and
// an httpOnly cookie this code never sees. Shortly before the token runs out it
// is traded at /Auth/refresh for a new one; the server ends the session after a
// week without a trade, or on a password change. A token without the mark is the
// ordinary two-hour sign-in and simply ends, as it always did.

/** Renew this long before expiry, so a request never leaves on a dying token. */
const RENEW_AHEAD_MS = 2 * 60 * 1000;

/** Whether a refresh cookie stands behind this token (the API's `renewable` claim). */
const isRenewable = (token) => {
  if (!token) return false;

  try {
    return JSON.parse(atob(token.split(".")[1])).renewable === "true";
  } catch (error) {
    return false;
  }
};

const expiresWithin = (token, ms) => {
  const expiresAt = getTokenExpirationTime(token);
  return !expiresAt || expiresAt.getTime() - Date.now() <= ms;
};

/**
 * One refresh in flight per tab, and — where the browser has Web Locks — one
 * per browser. Every tab shares the cookie, and the server replaces it on each
 * trade; tabs refreshing side by side would otherwise each spend it, and the
 * server would have to sort out which of them holds the live one. It does
 * tolerate that (a short grace), but the lock keeps it from being the norm.
 */
let refreshInFlight = null;

const withRefreshLock = (work) =>
  typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request("easyplanner-session-refresh", work)
    : work();

const tradeSessionCookie = async (staleToken) => {
  // Another tab may have renewed while this one waited for the lock: its new
  // token is already in localStorage, and asking again would only waste a turn
  // of the cookie.
  const current = localStorage.getItem(ACCESS_TOKEN);
  if (current && current !== staleToken && !expiresWithin(current, RENEW_AHEAD_MS)) {
    return current;
  }

  try {
    const response = await axios.post(`${API_BASE_URL}/Auth/refresh`, null, {
      // Same-origin in production; in development the API is on another
      // port, and the cookie only goes along when asked to.
      withCredentials: true,
    });
    const accessToken = response.data.accessToken;
    localStorage.setItem(ACCESS_TOKEN, accessToken);
    return accessToken;
  } catch (error) {
    const status = error.response?.status;
    // The server's answer that the session is over: signed out elsewhere,
    // a week unused, or a password change.
    if (status === 401 || status === 400) return null;
    // Anything else — offline, a deploy restarting the API, a rate limit —
    // says nothing about the session. The caller keeps it and tries again.
    throw error;
  }
};

/**
 * Trade the refresh cookie for a token to replace `staleToken`. Resolves to
 * the new token, to null when the session has ended, and rejects when the
 * server could not be asked (the session may well still be alive).
 */
export const refreshSession = (staleToken = localStorage.getItem(ACCESS_TOKEN)) => {
  if (!refreshInFlight) {
    refreshInFlight = withRefreshLock(() => tradeSessionCookie(staleToken)).finally(
      () => {
        refreshInFlight = null;
      },
    );
  }
  return refreshInFlight;
};

/**
 * The minute-by-minute check App runs: renews a remembered sign-in that is
 * about to run out, and reports whether there is still a session. Being unable
 * to reach the server counts as yes — the next check tries again — so a dropped
 * connection never costs anyone their sign-in.
 */
export const keepSessionAlive = async () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) return false;
  if (!isRenewable(token)) return !isTokenExpired(token);
  if (!expiresWithin(token, RENEW_AHEAD_MS)) return true;

  try {
    return (await refreshSession(token)) !== null;
  } catch (error) {
    return true;
  }
};

/** True when a remembered sign-in's token has run out and must be renewed before it can be used. */
export const sessionNeedsRenewal = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  return isRenewable(token) && isTokenExpired(token);
};

/**
 * Tell the API to forget this browser's remembered session. `fetch` with
 * keepalive rather than axios: logout() is usually followed at once by a
 * navigation to /login, which cancels an XHR mid-flight; a keepalive request
 * outlives the page. Fire-and-forget — the browser forgets the token either
 * way, and the session then dies of disuse within the week.
 */
const endRememberedSession = () => {
  try {
    fetch(`${API_BASE_URL}/Auth/logout`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch (error) {
    // No fetch in this environment; nothing to tell.
  }
};

// Logout function - clears all auth data and redirects to login
export const logout = (redirectToLogin = true) => {
  // Read before the tokens are removed. During a simulation the session that
  // may be remembered is the platform one set aside, not the token in use.
  const sessionToken =
    getImpersonation()?.originalToken ?? localStorage.getItem(ACCESS_TOKEN);
  if (isRenewable(sessionToken)) endRememberedSession();

  localStorage.removeItem(ACCESS_TOKEN);
  localStorage.removeItem("USER_EMAIL");
  // A platform session set aside for a simulation goes with it: signing out
  // must not leave a SystemOwner's token behind in this browser.
  localStorage.removeItem(IMPERSONATION);

  // Booking alerts stop with the session: the next person on this device must
  // not see the last one's customers on the lock screen. Unsubscribing in the
  // browser is enough — the push service then answers 410 and the server
  // forgets the device — and the next sign-in subscribes again by itself.
  forgetPushSubscription();

  if (redirectToLogin && window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// Check if user is authenticated with a valid token — or a remembered one that
// has run out and will be renewed before it is used.
export const isAuthenticated = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  return Boolean(token) && (!isTokenExpired(token) || isRenewable(token));
};

// Get remaining token time in minutes
export const getTokenRemainingTime = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) return 0;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expirationTime = payload.exp * 1000;
    const remainingMs = expirationTime - Date.now();
    return Math.max(0, Math.floor(remainingMs / 60000));
  } catch (error) {
    return 0;
  }
};

const getAuthHeader = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);

  // Check if token is expired before making request. A remembered one goes
  // out as it is: the request interceptor renews it and swaps the header.
  if (token && isTokenExpired(token) && !isRenewable(token)) {
    console.warn("Token expired, logging out...");
    logout();
    return {};
  }

  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * The language the UI is showing, for the Accept-Language header. The API
 * writes every message a person reads — validation errors, refusals, "check
 * your email" — in this language (ApiMessages on the backend); without it a
 * Romanian page showed the API's English as it came.
 *
 * resolvedLanguage is the bundle i18next actually rendered ("ro"/"en"). Before
 * it has initialised there is none, and the browser's own header is a fine
 * stand-in, so nothing is set. The widget sets the business's language through
 * i18next too, so its requests follow the same rule.
 */
export const uiLanguage = () => i18n.resolvedLanguage || i18n.language || null;

// Axios request interceptor to check token before each request
axios.interceptors.request.use(
  async (config) => {
    const language = uiLanguage();
    if (language) config.headers["Accept-Language"] = language;

    // Skip token check for public endpoints
    const publicEndpoints = [
      "/Auth/Login",
      "/Auth/SignUp",
      // Activation is reached from an emailed link, by someone who has never
      // signed in. A leftover expired token in localStorage must not be
      // allowed to reject the request before it is even sent.
      "/Auth/activate",
      "/Auth/resend-activation",
      // Reset is reached from an emailed link by someone who, by definition,
      // cannot sign in. A stale token in localStorage must not block it.
      "/Auth/forgot-password",
      "/Auth/reset-password",
      // The remembered session's own endpoints. They are called precisely
      // when the token has run out — the cookie is their credential — and
      // checking the token here would make refresh wait on itself.
      "/Auth/refresh",
      "/Auth/logout",
      // "Continue with Google": whether it is offered, and the trade of the
      // hand-off code at the end. Both happen before there is a session, and
      // a stale token left in this browser must not reject them.
      "/Auth/google",
      // A resource invite is reached from an emailed link too, by someone who
      // may never have had an account at all. Same reasoning as activate/reset:
      // a leftover expired token in localStorage must not reject the request.
      "/ResourceInvite/validate",
      "/ResourceInvite/accept",
      // An administrator invite is the same situation one level up: the person
      // following the link has no account at all until they redeem it, so a
      // stale token in this browser — a system owner testing their own link,
      // anyone on a shared machine — must not reject it before it is sent.
      "/AdminInvite/validate",
      "/AdminInvite/accept",
      // The manage link from a booking email. Its whole point is serving people
      // with no account at all, so a stale token left in this browser by anyone
      // — a previous user of a shared machine, the salon owner's own session —
      // must not reject the request before it is even sent.
      "/Booking/manage/",
      "/public/",
      "/available-slots/",
      "/available-dates/",
      // The rest of the anonymous booking flow. "/public/" above never matched
      // either: the page settings live under /slug/, and the booking is posted
      // to "/Booking/public" with no trailing slash. So a visitor carrying an
      // expired token from an old session was logged out and had the request
      // rejected before it was sent. On the widget it matters twice over — a
      // third-party iframe may not be allowed to touch localStorage at all, and
      // this check is the first thing that would.
      "/BookingPageSettings/slug/",
      "/Booking/public",
      // Discovery is the anonymous way into the platform — someone looking for
      // an appointment has no account yet. A stale token from a previous
      // session on this browser must not stop the search from being sent.
      "/Discovery/",
      // A company invitation is opened from a link passed on by hand, by someone
      // who by definition has no account yet. Same reasoning as the others: a
      // stale token in this browser must not reject the lookup before it is
      // sent. Only the preview half — creating and revoking invitations is
      // platform staff's, and stays authenticated.
      "/CompanyInvite/preview/",
      // The price list is what the marketing page shows before anyone has an
      // account. A stale token in this browser must not stop the page from
      // rendering its prices — the alternative is a pricing page with no
      // prices on it. Read-only; setting a price lives under /PlatformPricing
      // and stays authenticated.
      "/Pricing",
      // The contact form on the marketing page. Anyone using it is by
      // definition not signed in, and a stale token in this browser — the
      // owner's own expired session, anyone on a shared machine — must not
      // reject the message before it is even sent. This is the visitor's only
      // channel to us and it keeps no copy, so nothing may drop it silently.
      "/Contact",
      // Withdrawing from the contract. By law this has to work for someone who
      // cannot sign in — a lapsed subscriber, a closed account, somebody who
      // never finished signing up — so a stale token in this browser must not
      // reject the request before it is sent. That would turn a leftover
      // localStorage value into an obstacle to exercising a statutory right.
      "/Withdrawal",
    ];
    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      config.url?.includes(endpoint),
    );

    if (!isPublicEndpoint) {
      let token = localStorage.getItem(ACCESS_TOKEN);

      // A remembered sign-in about to run out is renewed first, and the
      // request leaves with the new token instead of the one its caller read.
      if (token && isRenewable(token) && expiresWithin(token, RENEW_AHEAD_MS)) {
        try {
          const renewed = await refreshSession(token);
          if (!renewed) {
            console.warn("[auth] Remembered session has ended, logging out...");
            logout();
            return Promise.reject(new Error("Session ended"));
          }
          token = renewed;
          if (config.headers?.Authorization) {
            config.headers.Authorization = `Bearer ${renewed}`;
          }
        } catch (error) {
          // The server could not be asked. While the old token still works
          // the request goes out on it; once it has run out the request fails
          // like any other without a connection — and the session is kept for
          // the next attempt rather than thrown away.
          if (isTokenExpired(token)) return Promise.reject(error);
        }
      }

      if (token && isTokenExpired(token)) {
        console.warn("Token expired before request, logging out...");
        logout();
        return Promise.reject(new Error("Token expired"));
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Axios response interceptor to handle 401 Unauthorized errors globally.
//
// A 401 is only treated as a dead session when our own token is actually
// missing or expired. It used to mean "log out" unconditionally, which handed
// any endpoint the power to end the session over a business rule: an account
// with no organization yet got a 401 from /Reports and was thrown back to the
// login page mid-onboarding. When the token is still valid, a 401 is the
// caller's problem to report, not grounds for destroying the session.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // A refresh that fails is answered by whoever asked for it (see
    // tradeSessionCookie); treating its 401 here as well would sign out twice.
    if (error.config?.url?.includes("/Auth/refresh")) {
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      const token = localStorage.getItem(ACCESS_TOKEN);
      // The API sets Token-Expired when it rejects an expired bearer token.
      const rejectedByServer =
        error.response.headers?.["token-expired"] === "true";

      // The server's clock is the one that counts: a remembered token it calls
      // expired is renewed and the request sent once more.
      const original = error.config;
      if (rejectedByServer && isRenewable(token) && original && !original.sessionRetried) {
        original.sessionRetried = true;
        try {
          const renewed = await refreshSession(token);
          if (renewed) {
            if (original.headers?.Authorization) {
              original.headers.Authorization = `Bearer ${renewed}`;
            }
            return axios(original);
          }
        } catch (refreshError) {
          // Could not reach the server to renew: not a reason to sign out.
          return Promise.reject(error);
        }
      }

      const sessionIsDead =
        !token || (isTokenExpired(token) && !isRenewable(token)) || rejectedByServer;

      if (sessionIsDead) {
        console.error("[auth] Session is no longer valid — logging out.");
        logout();
      } else {
        console.warn(
          `[auth] 401 from ${error.config?.url} with a valid token — not a session problem, leaving the user signed in.`,
        );
      }
    }
    return Promise.reject(error);
  },
);

/**
 * One page of accounts across the platform. Platform staff only.
 *
 * Paged, and with the auth header it was missing: the endpoint spans every
 * organization, so the unpaged version was a 13.7 MB response at seventy
 * thousand accounts.
 *
 * Returns `{ items, totalCount, page, pageSize, totalPages }`.
 */
export const GetUsers = async ({
  page = 1,
  pageSize = 50,
  search = "",
} = {}) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/User`, {
      params: { page, pageSize, search: search || undefined },
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * `loginRequest.rememberMe` asks for a remembered session as well: the
 * response then sets the refresh cookie, which a cross-origin request (local
 * development) only keeps when sent with credentials.
 */
export const LoginAction = async (loginRequest) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Auth/Login`,
      loginRequest,
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const SignupAction = async (signupRequest) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Auth/SignUp`,
      signupRequest,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// --- Continue with Google ---------------------------------------------------
//
// The whole OAuth flow runs through the API (GoogleAuthController): the button
// is a plain link to /Auth/google/start, Google sends the browser back to the
// API, and the API sends it on to /auth/google#code=… with a one-minute code
// that CompleteGoogleSignInAction trades for the same answer Login gives. No
// Google script is ever loaded here, so the CSP stays as it is.

/** Whether the button should be shown: false until the API has Google's keys. */
export const GetGoogleSignInStatusAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Auth/google`);
    return Boolean(response.data?.enabled);
  } catch (error) {
    throw error;
  }
};

/**
 * Where "Continue with Google" leads — a navigation, not a request: Google's
 * sign-in page can only be visited. `mode` is the page the button is on; the
 * signup page adds the account type and invitation, the login page "keep me
 * signed in". The terms version is the one shown beside the button, which the
 * API records as agreed to if it creates an account.
 */
export const googleSignInUrl = ({
  mode,
  role,
  inviteToken,
  termsVersion,
  rememberMe,
} = {}) => {
  const params = new URLSearchParams({ mode: mode === "signup" ? "signup" : "login" });
  if (role !== undefined && role !== null) params.set("role", String(role));
  if (inviteToken) params.set("invite", inviteToken);
  if (termsVersion) params.set("terms", termsVersion);
  if (rememberMe) params.set("remember", "true");
  const language = uiLanguage();
  if (language) params.set("lang", language);
  return `${API_BASE_URL}/Auth/google/start?${params.toString()}`;
};

/**
 * Trade the hand-off code for a session. With credentials, for the same reason
 * as LoginAction: a remembered sign-in comes back with the refresh cookie.
 */
export const CompleteGoogleSignInAction = async (code, rememberMe) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Auth/google/exchange`,
      { code, rememberMe: Boolean(rememberMe) },
      { withCredentials: true },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Redeem an activation link. `token` comes from the ?token= query parameter of
 * the emailed link.
 */
export const ActivateAccountAction = async (token) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Auth/activate`, {
      token,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Ask for a fresh activation link.
 *
 * Always resolves with the same generic message whether or not the address has
 * a pending account — the API refuses to confirm which emails are registered,
 * so there is nothing here to branch on.
 */
export const ResendActivationAction = async (email) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Auth/resend-activation`,
      {
        email,
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Ask for a password reset link.
 *
 * Resolves with the same generic message for every address — the API will not
 * confirm which emails are registered, so there is nothing here to branch on.
 */
export const ForgotPasswordAction = async (email) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Auth/forgot-password`, {
      email,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Check a reset link before showing the form. Returns `{ valid: boolean }` and
 * does not consume the token.
 */
export const ValidateResetTokenAction = async (token) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Auth/reset-password/validate`,
      { params: { token } },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Set a new password, spending the reset token. */
export const ResetPasswordAction = async (token, newPassword) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Auth/reset-password`, {
      token,
      newPassword,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const CreateOrganizationAction = async (organizationData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Organization`,
      organizationData,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetOrganizationAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Organization`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Asks ANAF's public register who a fiscal code belongs to, so nobody has to
 * type what the state already knows — and, more to the point, so a mistyped CUI
 * is caught here rather than by SPV, days after an invoice was issued against it.
 *
 * Never throws for a bad code. The endpoint answers 200 with a `status` of
 * "found", "notFound", "invalid" or "unavailable", because "no such company" is
 * a successful lookup with a negative answer, not an HTTP error. Only a network
 * or auth failure reaches the catch — and even then the caller lets the person
 * carry on typing, because a government service having a bad afternoon is not a
 * reason to block a signup.
 */
export const LookupCompanyByCuiAction = async (cui) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Organization/company-lookup`,
      { params: { cui }, headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UpdateOrganizationAction = async (organizationData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Organization`,
      organizationData,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetTotalBookingsCountAction = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Organization/bookings-count`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Whether the caller's organization has finished the setup wizard.
 * Membership-aware on the API side — works for the owner and for members.
 */
export const GetOrganizationSetupStatusAction = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Organization/setup-status`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Mark the caller's organization setup as complete. Idempotent — safe to call
 * more than once. Returns the updated OrganizationResponse.
 */
export const CompleteOrganizationSetupAction = async () => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Organization/complete-setup`,
      {},
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Persist the setup wizard's resume position (step 2-5) on the caller's
 * organization, so the wizard picks up where it left off on any device.
 * Returns the updated OrganizationResponse.
 */
export const UpdateSetupStepAction = async (step) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Organization/setup-step`,
      { step },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Resource Actions
export const GetResourcesAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Resource`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const CreateResourceAction = async (resourceData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Resource`,
      resourceData,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UpdateResourceAction = async (id, resourceData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Resource/${id}`,
      resourceData,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const DeleteResourceAction = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/Resource/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Resource Invite Actions

/**
 * Check a resource invite link before showing the accept page. Public — the
 * invitee has never signed in, or is signing in for the first time. Does not
 * consume the token.
 */
export const ValidateResourceInviteAction = async (token) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/ResourceInvite/validate`,
      {
        params: { token },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Redeem a resource invite link. `firstName`/`lastName`/`password` only
 * matter when validation reported `requiresPassword` — an existing
 * same-organization account is auto-linked from the token alone.
 *
 * Does not auto-login (matches AuthController.SignUp) — the invitee signs in
 * separately afterwards.
 */
export const AcceptResourceInviteAction = async ({
  token,
  firstName,
  lastName,
  password,
}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/ResourceInvite/accept`, {
      token,
      firstName,
      lastName,
      password,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Revoke any still-live invite for the resource and send a fresh one. */
export const ResendResourceInviteAction = async (resourceId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/ResourceInvite/${resourceId}/resend`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Revoke any still-live invite for the resource, without sending a replacement. */
export const RevokeResourceInviteAction = async (resourceId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/ResourceInvite/${resourceId}/revoke`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Service Actions
export const GetServicesAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Service`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetServicesByResourceAction = async (resourceId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Service/resource/${resourceId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const CreateServiceAction = async (serviceData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Service`, serviceData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UpdateServiceAction = async (id, serviceData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Service/${id}`,
      serviceData,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const DeleteServiceAction = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/Service/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Category Actions (optional service groupings, e.g. hospital departments)
export const GetCategoriesAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Category`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const CreateCategoryAction = async (categoryData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Category`,
      categoryData,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UpdateCategoryAction = async (id, categoryData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Category/${id}`,
      categoryData,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const DeleteCategoryAction = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/Category/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Booking Page Settings Actions
export const GetBookingPageSettingsAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/BookingPageSettings`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UploadAboutImageAction = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API_BASE_URL}/BookingPageSettings/about-images`, formData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const SaveBookingPageSettingsAction = async (
  settingsData,
  logoFile,
  headerImageFile,
) => {
  try {
    const formData = new FormData();

    // Every text field is optional. An omitted field arrives at the API as
    // null, which is what clears it — so blanking a field in the form and
    // saving removes it from the public site.
    const textFields = [
      "bookingTitle",
      "heroDescription",
      "ctaButtonText",
      "aboutText",
      "streetAddress",
      "bookingCity",
      "contactPhone",
      "contactEmail",
      "openingHours",
      "facebookUrl",
      "instagramUrl",
    ];
    textFields.forEach((field) => {
      const value = settingsData[field];
      if (value) formData.append(field, value);
    });

    formData.append("primaryColor", settingsData.primaryColor || "#5c9f24");
    formData.append("secondaryColor", settingsData.secondaryColor || "#1a1d19");
    formData.append("removeLogo", String(!!settingsData.removeLogo));
    formData.append("removeHeaderImage", String(!!settingsData.removeHeaderImage));

    // Append file fields
    if (logoFile) {
      formData.append("logoFile", logoFile);
    }
    if (headerImageFile) {
      formData.append("headerImageFile", headerImageFile);
    }

    const response = await axios.post(
      `${API_BASE_URL}/BookingPageSettings`,
      formData,
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetBookingPageBySlugAction = async (organizationSlug) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/BookingPageSettings/slug/${organizationSlug}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Public Booking Actions (no auth required)
export const GetPublicServicesAction = async (organizationSlug) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Service/public/${organizationSlug}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetPublicResourcesAction = async (organizationSlug) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Resource/public/${organizationSlug}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const GetAvailableTimeSlotsAction = async (
  organizationSlug,
  resourceId,
  serviceId,
  date,
) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Booking/available-slots/${organizationSlug}`,
      {
        params: { resourceId, serviceId, date },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * The days, from `from` ("yyyy-MM-dd") and `days` long, on which the resource
 * has at least one free slot for the service — "yyyy-MM-dd" strings, earliest
 * first. The booking modal's date strip shows only these.
 */
export const GetAvailableDatesAction = async (
  organizationSlug,
  resourceId,
  serviceId,
  from,
  days,
) => {
  const response = await axios.get(
    `${API_BASE_URL}/Booking/available-dates/${organizationSlug}`,
    {
      params: { resourceId, serviceId, from, days },
    },
  );
  return response.data;
};

export const CreatePublicBookingAction = async (bookingData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Booking/public`,
      bookingData,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Authenticated Booking Actions

/**
 * Get bookings for the user's organization with filters and pagination
 */
export const GetBookingsAction = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.status) params.append("status", filters.status);
    if (filters.resourceId) params.append("resourceId", filters.resourceId);
    if (filters.serviceId) params.append("serviceId", filters.serviceId);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
    if (filters.page) params.append("page", filters.page);
    if (filters.pageSize) params.append("pageSize", filters.pageSize);
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortDescending !== undefined)
      params.append("sortDescending", filters.sortDescending);

    const response = await axios.get(`${API_BASE_URL}/Booking?${params}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get the signed-in customer's own bookings, across every business they have
 * booked with.
 *
 * Takes no arguments on purpose: the API scopes the result to the email in the
 * JWT. There is no parameter here to widen, which is what keeps it from
 * becoming a lookup of other people's appointments.
 */
export const GetMyBookingsAction = async () => {
  const response = await axios.get(`${API_BASE_URL}/Booking/my`, {
    headers: getAuthHeader(),
  });
  return response.data;
};

/**
 * Slots the caller could move one of their own bookings to on `date`.
 *
 * Service and staff member come from the booking itself server-side, so there is
 * nothing to pass but the day. The booking's current slot is included — it is
 * still its own.
 *
 * @param {number} bookingId
 * @param {string} date - "YYYY-MM-DD"
 */
export const GetMyBookingSlotsAction = async (bookingId, date) => {
  const response = await axios.get(
    `${API_BASE_URL}/Booking/my/${bookingId}/available-slots?date=${encodeURIComponent(date)}`,
    { headers: getAuthHeader() },
  );
  return response.data;
};

/**
 * Change one of the caller's own bookings.
 *
 * `bookingDate` and `timeSlot` travel together or not at all — sending one alone
 * is rejected. Omit both to edit only the phone/notes.
 *
 * @param {number} bookingId
 * @param {{bookingDate?: string, timeSlot?: string, customerPhone?: string, notes?: string}} changes
 */
export const UpdateMyBookingAction = async (bookingId, changes) => {
  const response = await axios.put(
    `${API_BASE_URL}/Booking/my/${bookingId}`,
    changes,
    { headers: getAuthHeader() },
  );
  return response.data;
};

/** Cancel one of the caller's own bookings. */
export const CancelMyBookingAction = async (bookingId) => {
  const response = await axios.post(
    `${API_BASE_URL}/Booking/my/${bookingId}/cancel`,
    {},
    { headers: getAuthHeader() },
  );
  return response.data;
};

/**
 * Get bookings overlapping a date range, for the calendar.
 *
 * Unpaged, unlike GetBookingsAction: a calendar shows everything in the
 * visible window rather than the first page of it. `start` and `end` are ISO
 * strings. `mine` narrows the result to the caller's own resources; the API
 * confines employees to their own bookings regardless of what is passed here.
 */
export const GetCalendarBookingsAction = async (start, end, mine = false) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Booking/calendar`, {
      params: { start, end, mine },
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get the users in the caller's organization (for assigning a resource owner)
 */
export const GetOrganizationMembersAction = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/User/organization-members`,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create an employee inside the caller's organization
 */
export const CreateEmployeeAction = async (employeeData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/User/employees`,
      employeeData,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get a single booking by ID
 */
export const GetBookingByIdAction = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Booking/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a new booking (authenticated)
 */
export const CreateBookingAction = async (bookingData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Booking`, bookingData, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update an existing booking
 */
export const UpdateBookingAction = async (id, bookingData) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Booking/${id}`,
      bookingData,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update booking status
 */
export const UpdateBookingStatusAction = async (id, status) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/Booking/${id}/status`,
      { status },
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Delete a booking
 */
export const DeleteBookingAction = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/Booking/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get available time slots (authenticated)
 */
/**
 * `excludeBookingId` is the booking being edited. Without it the list comes back
 * without the slot that booking already occupies, so the edit form cannot
 * preselect its own time.
 */
export const GetAvailableTimeSlotsAuthAction = async (
  resourceId,
  serviceId,
  date,
  excludeBookingId = null,
) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Booking/available-slots`,
      {
        params: { resourceId, serviceId, date, excludeBookingId },
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user info from JWT token
 */
export const getUserInfoFromToken = () => {
  const token = localStorage.getItem(ACCESS_TOKEN);
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return {
      email: payload.email,
      role: parseInt(payload.role, 10),
      organizationId: payload.organizationId
        ? parseInt(payload.organizationId, 10)
        : null,
      userId: payload.userId ? parseInt(payload.userId, 10) : null,
      // Set only on a simulation token: the SystemOwner behind it. Everything
      // else in this payload is the company owner being simulated.
      impersonatorId: payload.impersonatorId
        ? parseInt(payload.impersonatorId, 10)
        : null,
    };
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Reports Actions

/**
 * Get dashboard reports with analytics data
 * @param {string} period - Predefined period: 7days, 30days, 90days, year
 * @param {string} startDate - Custom start date (optional)
 * @param {string} endDate - Custom end date (optional)
 */
export const GetDashboardReportsAction = async (
  period = "30days",
  startDate = null,
  endDate = null,
) => {
  try {
    const params = new URLSearchParams();
    if (period) params.append("period", period);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    const response = await axios.get(`${API_BASE_URL}/Reports?${params}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Support Ticket Actions

/**
 * Open a support ticket. Sent as multipart because it may carry an image.
 * @param {{category:number, subject:string, description:string}} ticket
 * @param {File|null} imageFile - optional screenshot/photo (max 5MB, images only)
 */
export const CreateSupportTicketAction = async (ticket, imageFile = null) => {
  try {
    const formData = new FormData();
    formData.append("category", ticket.category);
    formData.append("subject", ticket.subject);
    formData.append("description", ticket.description);
    if (imageFile) {
      formData.append("imageFile", imageFile);
    }

    const response = await axios.post(
      `${API_BASE_URL}/SupportTicket`,
      formData,
      {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** The tickets the caller may see (scope decided server-side by role). */
export const GetSupportTicketsAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/SupportTicket`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** A single ticket with its full conversation thread. */
export const GetSupportTicketAction = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/SupportTicket/${id}`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Post a reply on a ticket. Returns the updated ticket with the new message. */
export const AddSupportTicketMessageAction = async (id, body) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/SupportTicket/${id}/messages`,
      { body },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Change a ticket's status. Staff only (owner / assigned admin). */
export const UpdateSupportTicketStatusAction = async (id, status) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/SupportTicket/${id}/status`,
      { status },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Admin Assignment Actions (System Owner only)
//
// The list of organizations to assign now comes from
// `GetPlatformCompaniesAction`, which already carries the assigned admin along
// with everything else about a business. These two are the mutation half.

/** The admins available to assign to organizations. */
export const GetAdminsAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/User/admins`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Assign an organization to an admin, or clear it with adminUserId = null. */
export const AssignAdminAction = async (organizationId, adminUserId) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Organization/${organizationId}/assign-admin`,
      { adminUserId },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Platform Admin Actions (platform staff only)
//
// The dashboard for the people who run EasyPlanner. The API narrows every figure
// to what the caller's role may see — the whole platform for a system owner,
// only their assigned organizations for an admin — so nothing here identifies
// whose numbers are being asked for.

/** Growth, totals and plan mix over the last `months` calendar months. */
export const GetPlatformOverviewAction = async (months = 12) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/PlatformAdmin/overview?months=${months}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * One page of the businesses the caller is responsible for, alphabetically.
 *
 * Searching and filtering happen server-side. They used to happen here, over a
 * response that carried every company on the platform — which at ten thousand
 * of them was a 5.8 MB download to answer a question about six.
 *
 * Returns `{ items, totalCount, page, pageSize, totalPages }`.
 */
export const GetPlatformCompaniesAction = async ({
  page = 1,
  pageSize = 25,
  search = "",
  status = "all",
  licence = "all",
} = {}) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/PlatformAdmin/companies`,
      {
        params: {
          page,
          pageSize,
          search: search || undefined,
          // "all" is the absence of a filter, not a value the server knows.
          status: status !== "all" ? status : undefined,
          licence: licence !== "all" ? licence : undefined,
        },
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * One business and its booking report. Answers 404 for a company outside the
 * caller's scope, so a guessed id reveals nothing.
 */
export const GetPlatformCompanyAction = async (
  organizationId,
  period = "30days",
) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/PlatformAdmin/companies/${organizationId}?period=${period}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Simulation ("open as owner")
//
// A SystemOwner opens a business as its owner sees it, to set it up for them.
// The API hands back a token in the owner's name, fenced server-side to the
// wizard, calendar, bookings, services, team and site. The platform token is
// set aside in localStorage and put back when the simulation ends, so leaving
// it does not mean signing in again.

/** Ask the API for a simulation token for one business. SystemOwner only. */
export const StartImpersonationAction = async (organizationId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/PlatformAdmin/companies/${organizationId}/impersonate`,
      null,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** The simulation set aside in this browser, or null when there is none. */
export const getImpersonation = () => {
  try {
    const raw = localStorage.getItem(IMPERSONATION);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Switch this browser to a simulation token, keeping the platform token aside.
 *
 * Starting a second simulation from inside the first keeps the *original*
 * platform token — never the first simulation's — so leaving always lands
 * back on the SystemOwner's own session.
 */
export const beginImpersonation = ({ accessToken, businessName, ownerEmail }) => {
  const originalToken =
    getImpersonation()?.originalToken ?? localStorage.getItem(ACCESS_TOKEN);

  localStorage.setItem(
    IMPERSONATION,
    JSON.stringify({ originalToken, businessName, ownerEmail }),
  );
  localStorage.setItem(ACCESS_TOKEN, accessToken);
};

/**
 * Leave the simulation and put the platform token back.
 *
 * Returns false when there was nothing to go back to — the platform session
 * expired meanwhile — in which case this browser is signed out instead. A
 * remembered platform token that ran out during the simulation still counts:
 * it is renewed on the way back, like any other.
 */
export const endImpersonation = () => {
  const originalToken = getImpersonation()?.originalToken;
  localStorage.removeItem(IMPERSONATION);

  if (originalToken && (!isTokenExpired(originalToken) || isRenewable(originalToken))) {
    localStorage.setItem(ACCESS_TOKEN, originalToken);
    return true;
  }

  logout(false);
  return false;
};

// Company Invitation Actions
//
// Links that bring a business onto the platform. The first three are platform
// staff's; the fourth is opened by whoever received the link and has no account
// yet, which is why it is in `publicEndpoints` above.

/** The invitations this caller may see — their own, or all for a system owner. */
export const GetCompanyInvitesAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/CompanyInvite`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Mint a new invitation link. */
export const CreateCompanyInviteAction = async (request) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/CompanyInvite`,
      request,
      {
        headers: getAuthHeader(),
      },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Withdraw an invitation nobody has redeemed yet. */
export const RevokeCompanyInviteAction = async (inviteId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/CompanyInvite/${inviteId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * What the signup page may know about an invitation token. Answers 200 for a
 * bad token as readily as a good one — `isValid` carries the verdict — so this
 * never throws on an expired or mistyped link.
 */
export const PreviewCompanyInviteAction = async (token) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/CompanyInvite/preview/${encodeURIComponent(token)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Admin Invite Actions
//
// Invitations that grant the platform-staff role. The first four are a system
// owner's alone — an admin cannot invite peers, and the API refuses them twice
// over. The last two are opened by the invitee, who has no account until they
// use the link, which is why they sit in `publicEndpoints` above.

/** Every administrator invitation ever issued, newest first. */
export const GetAdminInvitesAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/AdminInvite`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Invite one person to become an administrator. Emails them the link. */
export const CreateAdminInviteAction = async (request) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/AdminInvite`, request, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Revoke the current link and email a fresh one to the same address. The old
 * link stops working, which is also how a link that went astray is replaced.
 */
export const ResendAdminInviteAction = async (inviteId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/AdminInvite/${inviteId}/resend`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Withdraw an invitation nobody has redeemed yet. */
export const RevokeAdminInviteAction = async (inviteId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/AdminInvite/${inviteId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Check an administrator invite link before showing the accept form. Public —
 * the invitee has never had an account. Answers 200 for a bad token as readily
 * as a good one; `valid` carries the verdict.
 */
export const ValidateAdminInviteAction = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/AdminInvite/validate`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Redeem an administrator invite link, creating the staff account. No email is
 * sent up — it comes from the invitation, not from the person redeeming it.
 *
 * Does not auto-login (matches AuthController.SignUp and the resource-invite
 * accept) — the new admin signs in afterwards.
 */
export const AcceptAdminInviteAction = async ({
  token,
  firstName,
  lastName,
  password,
}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/AdminInvite/accept`, {
      token,
      firstName,
      lastName,
      password,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// My Profile Actions
//
// The caller's own account details. Identity-scoped like the account-deletion
// pair below: the subject is the token's, so no user id is ever sent.

/** Name, email, phone and city of residence for the signed-in account. */
export const GetMyProfileAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/User/me`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Save the editable part of the profile. Email is not in the payload — it is the
 * login identity, and the API would ignore it anyway.
 */
export const UpdateMyProfileAction = async (profile) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/User/me`, profile, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Turn this personal account into a business owner's. Answers `{ accessToken }`:
 * the role lives in the token, so the caller stores the new one before going on
 * to the organization wizard.
 */
export const OpenProfessionalAccountAction = async () => {
  try {
    const response = await axios.post(`${API_BASE_URL}/User/me/professional`, null, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Account deletion (GDPR art. 17)
//
// All three act on the identity in the token — no user id is ever sent, because
// an endpoint that erased whatever id it was handed would be a way to delete
// other people's accounts.

/** Whether a deletion is pending, when it runs, and what it would take with it. */
export const GetAccountDeletionStatusAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Account/deletion`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Request erasure. Suspends the account now; the scrub runs after the grace period. */
export const RequestAccountDeletionAction = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Account/deletion`,
      {},
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Stop a pending deletion, while the grace window is still open. */
export const CancelAccountDeletionAction = async () => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/Account/deletion`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Booking Manage-Link Actions
//
// The emailed link is the only credential these carry — no auth header, by
// design. Everything here serves a customer who booked as a guest and has no
// account to sign in with.

/** The booking behind a manage link. Rejects with a 404 if the link is dead. */
export const GetBookingByManageTokenAction = async (token) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Booking/manage/${encodeURIComponent(token)}`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Slots this booking could move to on `date` (YYYY-MM-DD). */
export const GetManageAvailableSlotsAction = async (token, date) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Booking/manage/${encodeURIComponent(token)}/available-slots`,
      { params: { date } },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Move the booking. `bookingDate` and `timeSlot` go together or not at all. */
export const UpdateBookingByManageTokenAction = async (token, payload) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Booking/manage/${encodeURIComponent(token)}`,
      payload,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const CancelBookingByManageTokenAction = async (token) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Booking/manage/${encodeURIComponent(token)}/cancel`,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Bookings created or cancelled since `since` (an ISO string from a previous
 * response's `serverTimeUtc`).
 *
 * Omit `since` for the first call: the API answers with a cursor and no events,
 * which is what starts the session without replaying the day as popups.
 */
export const GetBookingActivityAction = async (since) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Booking/activity`, {
      headers: getAuthHeader(),
      params: since ? { since } : undefined,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * The VAPID public key this device's browser subscribes to booking alerts
 * with: `{ publicKey }`, where `publicKey` is null when the server sends no
 * Web Push and the dashboard should keep its in-tab notifications.
 */
export const GetPushConfigAction = async () => {
  const response = await axios.get(`${API_BASE_URL}/PushSubscription/config`, {
    headers: getAuthHeader(),
  });
  return response.data;
};

/**
 * Registers this device's push subscription for the signed-in staff member.
 * Idempotent — sent on every dashboard load, which is how a new password, a
 * rotated key or a changed UI language reaches the stored row.
 * `{ endpoint, p256dh, auth }`, from `PushSubscription.toJSON()`.
 */
export const SavePushSubscriptionAction = async (subscription) => {
  await axios.put(`${API_BASE_URL}/PushSubscription`, subscription, {
    headers: getAuthHeader(),
  });
};

// Notification Settings Actions

/**
 * The organization's notification preferences. Always resolves for a member of
 * an organization — the API materialises the defaults when nothing has been
 * saved, so there is no "not configured yet" state to handle here.
 */
export const GetNotificationSettingsAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/NotificationSettings`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Save the whole preferences form. Every flag is sent on every save: an absent
 * key would be read as `false` by the API, so a partial payload silently turns
 * notifications off.
 */
export const SaveNotificationSettingsAction = async (settings) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/NotificationSettings`,
      settings,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Subscription Actions

/** The caller's organization plan plus live usage against its limits. */
export const GetSubscriptionAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Subscription`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Open a payment for the organization's current plan.
 *
 * Sends no amount — the server reads what this subscription was sold at and
 * converts it at the exchange rate the organization is held to. A client that
 * could name its own price would be a client that could buy the top plan for a
 * leu.
 *
 * Returns `{ orderId, paymentUrl, amountCents, currency, ... }`. The caller
 * sends the browser to `paymentUrl`, which is NETOPIA's own page: no card
 * details ever reach this application.
 */
export const StartPaymentAction = async (periodMonths = 1) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Payment/start`,
      { periodMonths },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** The organization's payments, newest first. */
export const GetPaymentHistoryAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Payment`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * One payment, by our order reference — what the page the customer lands on
 * after paying reads.
 *
 * It reports; it does not grant. A payment only counts once NETOPIA has told
 * our server so, over a signed notification, so this can legitimately still say
 * "pending" for a few seconds after a successful payment.
 */
export const GetPaymentByOrderIdAction = async (orderId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Payment/${encodeURIComponent(orderId)}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// SMS Centre Actions

/** Credit, packages, settings and recent activity, in one read. */
export const GetSmsCenterAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Sms/center`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Opens a NETOPIA payment for one SMS package. Only the package code is sent —
 * the price is the server's. The answer has the same shape as
 * StartPaymentAction's: the lei amount to show, and where to send the card.
 */
export const StartSmsPurchaseAction = async (packageCode) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/Sms/purchase`,
      { packageCode },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Saves the low-credit warning and returns the refreshed centre. */
export const UpdateSmsSettingsAction = async (settings) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/Sms/settings`, settings, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * One invoice as a PDF, for the download button on the billing page.
 *
 * Goes through our own API rather than linking to Oblio. Their URL carries its
 * own access token and opens the document for anyone who has it, so it never
 * reaches the browser — no page source, no history entry, nothing to paste by
 * accident. Resolves to a Blob; the caller names the file.
 */
export const DownloadInvoiceAction = async (invoiceId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/Invoice/${encodeURIComponent(invoiceId)}/pdf`,
      { headers: getAuthHeader(), responseType: "blob" },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Record a consumer withdrawing from the contract, from the public form.
 *
 * Unauthenticated on purpose — the right belongs to people who may have no
 * account left to sign in to. Resolves to `{ id, receivedAt, confirmationSent }`;
 * `receivedAt` is the date the fourteen days run from, so the page shows it back.
 */
export const SubmitWithdrawalAction = async (request) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Withdrawal`, request);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Change the organization's plan. Returns updated usage. */
export const UpdatePlanAction = async (plan) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/Subscription/plan`,
      { plan },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get public platform statistics for the landing page
 * No authentication required
 */
export const GetPlatformStatsAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/PlatformStats`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get organizations with booking pages for the portfolio section
 * No authentication required
 */
export const GetPortfolioOrganizationsAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/PlatformStats/portfolio`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Search businesses across the whole platform.
 *
 * Without a `date` each business still reports availability — its soonest in
 * the coming week — so the results answer "when could I be seen" rather than
 * just "who exists".
 *
 * `filters` mirrors the discovery page's controls; blank values are dropped
 * rather than sent, so a cleared dropdown is absent from the query string
 * instead of arriving as an empty filter.
 *
 * No authentication required — this is how someone with no account finds a
 * business in the first place.
 */
export const SearchBusinessesAction = async (filters = {}) => {
  const params = {};
  ["q", "city", "category", "date", "partOfDay", "sort"].forEach((key) => {
    if (filters[key]) params[key] = filters[key];
  });
  if (filters.availableOnly) params.availableOnly = true;
  if (filters.page) params.page = filters.page;
  if (filters.pageSize) params.pageSize = filters.pageSize;

  try {
    const response = await axios.get(`${API_BASE_URL}/Discovery/search`, {
      params,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * The cities, categories and service names worth offering as filters.
 * No authentication required.
 */
export const GetDiscoveryFiltersAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Discovery/filters`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// --- Contact -----------------------------------------------------------------

/**
 * Send a message from the public contact form on the marketing page.
 *
 * Anonymous — the visitor has no account and there is no organization behind
 * the request. The API mails it to the monitored company inbox and keeps no
 * copy, so a rejected promise here means the message genuinely did not go and
 * the page has to say so rather than thanking the visitor.
 *
 * `website` is the honeypot: the form renders it hidden and empty, and the
 * server drops anything that arrives with it filled in. Pass it through
 * untouched — stripping it client-side would disarm the check.
 */
export const SendContactMessageAction = async ({
  name,
  email,
  subject,
  message,
  website = "",
}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/Contact`, {
      name,
      email,
      subject,
      message,
      website,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// --- Pricing -----------------------------------------------------------------

/**
 * What the plans cost right now, with any live promotion already applied.
 *
 * Anonymous: the marketing page calls it before anyone has signed in. Every
 * screen that shows a price goes through here rather than holding its own copy
 * of the numbers, so the pricing page, the signup wizard and the plan panel
 * cannot quote three different figures.
 */
export const GetPublicPricingAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/Pricing`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Prices, the full price history and every promotion. SystemOwner only. */
export const GetPlatformPricingAction = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/PlatformPricing`, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Set a plan's price. Adds a version rather than editing one — businesses
 * already on the plan keep what they were sold at.
 */
export const SetPlanPriceAction = async (request) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/PlatformPricing/plans`,
      request,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Set how many days a new business gets on a paid plan before paying.
 *
 * Zero means no trial at all. Applies to trials started afterwards — a business
 * already inside one keeps the end date it was given.
 */
export const SetTrialDaysAction = async (days) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/PlatformPricing/trial-days`,
      { days },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Set whether a business appears in the public directory at /discover.
 *
 * SystemOwner only — the server refuses an Admin as well as hiding the control.
 * Overrides what the invoices would decide, in either direction.
 */
export const SetDirectoryListingAction = async (organizationId, listing) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/PlatformAdmin/companies/${organizationId}/directory-listing`,
      { listing },
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/** Withdraw a scheduled price change that has not taken effect yet. */
export const DeleteScheduledPriceAction = async (planPriceId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/PlatformPricing/plans/${planPriceId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const CreatePromotionAction = async (request) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/PlatformPricing/promotions`,
      request,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UpdatePromotionAction = async (promotionId, request) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/PlatformPricing/promotions/${promotionId}`,
      request,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Remove a promotion outright. Refused once anything was sold under it —
 * switching it off is the supported way to stop one.
 */
export const DeletePromotionAction = async (promotionId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/PlatformPricing/promotions/${promotionId}`,
      { headers: getAuthHeader() },
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};
